import type { User } from "oidc-client-ts";
import { Log } from "oidc-client-ts";
import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import type { AuthProviderNoUserManagerProps } from "react-oidc-context";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { useLocation } from "react-router";

import { AuthLoader } from "authentication/AuthLoader";
import { oidcAuthority, oidcClientId } from "authentication/constants";
import { OidcUserStore } from "authentication/OidcUserStore";
import { TokenManagerSingleton } from "authentication/TokenManager";
import { coordinatedSigninSilent } from "authentication/tokenRefreshLock";
import { setIsAuthenticatingCookie } from "common/utilities/cookies";
import { isDevOrStaging } from "common/utilities/environment";
import { readOidcUserFromLocalStorage } from "common/utilities/localStorage";
import { logger } from "common/utilities/logger";
import { useDispatch } from "common/utilities/react-redux";
import { useConst } from "common/utilities/useConst";
import { unauthenticate } from "domains/authentication/actions";
import { activateModal } from "domains/modal/actions";
import { useFetchPermissions } from "entities/permissions/useFetchPermissions";

if (isDevOrStaging) {
  Log.setLogger(console);
  // Enable maximal logging of oidc-client-ts
  Log.setLevel(Log.DEBUG);
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const fetchPermissions = useFetchPermissions();
  const { pathname } = useLocation();

  const onSigninCallback = (user: User | undefined) => {
    if (user === undefined) {
      logger.error(`[onSigninCallback] OIDC user is ${user}`);
      setIsAuthenticatingCookie(false);
      return;
    }
    if (user.id_token === undefined) {
      logger.error("[onSigninCallback] ID token is missing");
      setIsAuthenticatingCookie(false);
      return;
    }
    void fetchPermissions(user.id_token);
  };

  // We declare props in this way because the `OidcAuthProvider` type definition is bugged (too permissive).
  const props: AuthProviderNoUserManagerProps = {
    authority: oidcAuthority,
    // We handle silent renewals manually so that we can control whether the loading spinner renders.
    automaticSilentRenew: false,
    client_id: oidcClientId,
    onSigninCallback,
    // This property is required, but its value is never used. We always pass a redirect URI to `signinRedirect`.
    redirect_uri: import.meta.env.VITE_WEBSITE_URL,
    // Ignore (rather than consuming) code/state query params when they are for the mobile app.
    skipSigninCallback: pathname === "/app-redirect",
    // This class does not maintain any significant internal state, so reinstantiating on each render is fine.
    userStore: new OidcUserStore(),
  };

  return (
    <OidcAuthProvider {...props}>
      <AuthLoaderWrapper>{children}</AuthLoaderWrapper>
    </OidcAuthProvider>
  );
};

const AuthLoaderWrapper = ({ children }: PropsWithChildren) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const fetchPermissions = useFetchPermissions();
  const [isSilentlyRenewing, setIsSilentlyRenewing] = useState<boolean>(false);

  const addAccessTokenExpired = useConst(() => auth.events.addAccessTokenExpired.bind(auth.events));
  const addAccessTokenExpiring = useConst(() => auth.events.addAccessTokenExpiring.bind(auth.events));
  const removeUser = useConst(() => auth.removeUser.bind(auth));
  const revokeTokens = useConst(() => auth.revokeTokens.bind(auth));
  const signinSilent = useConst(() => auth.signinSilent.bind(auth));

  useEffect(() => {
    const signOut = () =>
      fetch(`${import.meta.env.VITE_LOGIN_URL}/ssologin/logout`, { credentials: "include" })
        .then(
          () =>
            void Promise.allSettled([
              revokeTokens(["access_token"]).catch((error: unknown) =>
                logger.errorLocal("[signOut] Failed to revoke tokens", error),
              ),
              removeUser().catch((error: unknown) => logger.errorLocal("[signOut] Failed to remove user", error)),
            ]),
        )
        .catch((error: unknown) => logger.errorLocal("[signOut] Failed to logout user", error));

    const renewTokens = async (isSilent = true) => {
      setIsSilentlyRenewing(isSilent);

      try {
        const currentOidcUser = readOidcUserFromLocalStorage();
        const idToken = await coordinatedSigninSilent({
          fetchPermissions,
          getCurrentOidcUser: readOidcUserFromLocalStorage,
          knownIdToken: currentOidcUser?.id_token,
          knownRefreshToken: currentOidcUser?.refresh_token,
          signinSilent,
        });
        if (idToken === undefined) throw new Error("[renewTokens] Failed to acquire refreshed token");
        return idToken;
      } catch (error) {
        logger.error("[renewTokens] Failed to renew tokens", error);
        dispatch(unauthenticate());
        dispatch(activateModal({ id: "Unauthenticated" }));
        return undefined;
      } finally {
        setIsSilentlyRenewing(false);
      }
    };

    TokenManagerSingleton.setTokenManagerFunctions({ signOut, renewTokens });
  }, [dispatch, fetchPermissions, removeUser, revokeTokens, signinSilent]);

  useEffect(() => {
    const renewTokensLoudly = () => void TokenManagerSingleton.renewTokens(false);
    const renewTokensSilently = () => void TokenManagerSingleton.renewTokens();

    const removeAccessTokenExpired = addAccessTokenExpired(renewTokensLoudly);
    const removeAccessTokenExpiring = addAccessTokenExpiring(renewTokensSilently);

    return () => {
      removeAccessTokenExpired();
      removeAccessTokenExpiring();
    };
  }, [addAccessTokenExpired, addAccessTokenExpiring]);

  return <AuthLoader isAuthLoading={auth.isLoading && !isSilentlyRenewing}>{children}</AuthLoader>;
};
